import { UserButton, useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { NotificationCenter } from "../components/NotificationCenter";
import { Case, CategoryKey, QuickFilterKey, SortMode } from "../types/home";
import { CATEGORIES, extractPrimaryRequest } from "../utils/homeCaseGrouping";
import { useHomeCasesController } from "../hooks/useHomeCasesController";
import {
  IconCog,
  IconX,
  IconExclamation,
  IconPlus,
  IconSearch,
  IconFolder,
} from "../components/Home/Icons";
import { HomeHeader } from "../components/Home/HomeHeader";
import { HomeToolbar } from "../components/Home/HomeToolbar";
import { CaseCategorySection } from "../components/Home/CaseCategorySection";
import { useSubscription } from "../hooks/useSubscription";

// ============================================
// DELETE MODAL
// ============================================

function DeleteModal({
  caseName,
  primaryRequest,
  isDeleting,
  onConfirm,
  onCancel,
}: {
  caseName: string;
  primaryRequest: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onCancel}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-all">
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <IconX className="w-5 h-5" />
          </button>

          <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center mb-4 ring-8 ring-red-50">
            <IconExclamation className="w-7 h-7 text-red-600" />
          </div>

          <h3 className="text-xl font-semibold text-slate-900 text-center mb-2">
            Delete Case
          </h3>
          <p className="text-slate-600 text-center mb-6">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-slate-900">
              {primaryRequest || `Patient ${caseName}`}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 shadow-md shadow-red-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Deleting...
                </>
              ) : (
                "Delete Case"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE
// ============================================

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-12 text-center">
      <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center mb-4 ring-8 ring-teal-50">
        <IconFolder className="w-8 h-8 text-teal-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">
        No cases yet
      </h3>
      <p className="text-slate-500 mb-6 max-w-sm mx-auto">
        Create your first case to get started. We'll help you generate PA
        letters and prepare for peer-to-peer reviews.
      </p>
    </div>
  );
}

// ============================================
// NO RESULTS STATE
// ============================================

function NoResultsState({
  searchTerm,
  quickFilter,
  onClear,
}: {
  searchTerm: string;
  quickFilter: QuickFilterKey;
  onClear: () => void;
}) {
  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasQuickFilter = quickFilter !== "all";

  let message = "No matching cases";
  if (hasSearchTerm && hasQuickFilter) {
    message = `No cases match "${searchTerm}" with the selected filter`;
  } else if (hasSearchTerm) {
    message = `No cases match "${searchTerm}"`;
  } else if (hasQuickFilter) {
    message = `No cases match the selected filter`;
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-12 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <IconSearch className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">
        No cases found
      </h3>
      <p className="text-slate-500 mb-4">{message}</p>
      <button
        onClick={onClear}
        className="text-teal-600 hover:text-teal-700 font-medium text-sm"
      >
        Clear filters
      </button>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

// Show filter toolbar only when user has enough cases to benefit from it
const FILTER_BAR_THRESHOLD = 10;

export default function Home() {
  const { user } = useUser();
  const { tier } = useSubscription();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Case | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [quickFilter, setQuickFilter] = useState<QuickFilterKey>("all");
  const [sortMode, setSortMode] = useState<SortMode>("recent");
  const [expandedCategories, setExpandedCategories] = useState<
    Record<CategoryKey, boolean>
  >({
    medication: true,
    imaging: true,
    procedure: true,
    referral: true,
    dme: true,
    other: true,
  });

  // Use the controller hook
  const {
    filteredCases,
    groupedCases,
    nonEmptyCategories,
    availableFilters,
    totalCasesCount,
    filteredCasesCount,
    nonEmptyCategoriesCount,
    allExpanded,
    hasResults,
    isFiltering,
  } = useHomeCasesController({
    cases,
    searchQuery: searchTerm,
    quickFilter,
    sortMode,
    expandedCategories,
  });

  useEffect(() => {
    async function fetchCases() {
      if (!user) return;

      const { data, error } = await supabase
        .from("cases")
        .select(
          "id, friendly_name, request_type, request_details, insurance_company, denial_reason, diagnoses, created_at, updated_at, status, case_status, appeal_status, is_submitted, is_starred"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching cases:", error);
      } else {
        setCases(data || []);
      }
      setLoading(false);
    }

    fetchCases();
  }, [user]);

  const toggleCategory = (key: CategoryKey) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const expandAll = () => {
    setExpandedCategories({
      medication: true,
      imaging: true,
      procedure: true,
      referral: true,
      dme: true,
      other: true,
    });
  };

  const collapseAll = () => {
    setExpandedCategories({
      medication: false,
      imaging: false,
      procedure: false,
      referral: false,
      dme: false,
      other: false,
    });
  };

  const handleDeleteClick = (e: React.MouseEvent, caseItem: Case) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(caseItem);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);

    const { error } = await supabase
      .from("cases")
      .delete()
      .eq("id", deleteTarget.id);

    if (error) {
      console.error("Error deleting case:", error);
      alert("Failed to delete case. Please try again.");
    } else {
      setCases((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    }

    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setDeleteTarget(null);
    }
  };

  // ── Star toggle (optimistic) ────────────────────────────────

  const handleStar = async (caseId: string, starred: boolean) => {
    setCases((prev) =>
      prev.map((c) => (c.id === caseId ? { ...c, is_starred: starred } : c))
    );

    const { error } = await supabase
      .from("cases")
      .update({ is_starred: starred })
      .eq("id", caseId);

    if (error) {
      console.error("Failed to update star:", error);
      setCases((prev) =>
        prev.map((c) => (c.id === caseId ? { ...c, is_starred: !starred } : c))
      );
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setQuickFilter("all");
  };

  const showFilterBar = totalCasesCount >= FILTER_BAR_THRESHOLD;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteModal
          caseName={deleteTarget.friendly_name}
          primaryRequest={extractPrimaryRequest(
            deleteTarget.request_type,
            deleteTarget.request_details
          )}
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {/* Header */}
      <header className="bg-white/80 border-b border-slate-200 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logomark + wordmark */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm shadow-teal-500/20">
              <svg
                className="w-[18px] h-[18px] text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">
              <span className="text-teal-500">Peer</span>Prep
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Upgrade nudge — free users only */}
            {tier === "free" && (
              <Link
                to="/pricing"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-teal-600 text-white text-xs font-semibold shadow-sm shadow-teal-500/20 hover:from-teal-600 hover:to-teal-700 transition-all"
              >
                Upgrade
              </Link>
            )}
            {/* Trust badge — paid users only */}
            {tier !== "free" && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200/60 text-[11px] font-medium text-emerald-700">
                <svg
                  className="w-3 h-3 text-emerald-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                  />
                </svg>
                De-identified by design
              </span>
            )}
            <NotificationCenter />
            <Link
              to="/settings"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              title="Settings"
            >
              <IconCog className="w-5 h-5" />
            </Link>
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Page Header */}
        <HomeHeader
          totalCasesCount={totalCasesCount}
          filteredCasesCount={filteredCasesCount}
          nonEmptyCategoriesCount={nonEmptyCategoriesCount}
          isFiltering={isFiltering}
        />

        {/* Search + Filter Bar — only shown at 10+ cases */}
        {!loading && showFilterBar && (
          <HomeToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showCollapseButton={nonEmptyCategories.length > 1}
            allExpanded={allExpanded}
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            availableFilters={availableFilters}
            activeFilter={quickFilter}
            onFilterChange={setQuickFilter}
            sortMode={sortMode}
            onSortChange={setSortMode}
          />
        )}

        {/* Compact search for small lists (under threshold) */}
        {!loading && !showFilterBar && totalCasesCount > 0 && (
          <div className="mb-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white placeholder-slate-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20"
                placeholder="Search cases..."
              />
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-12 text-center">
            <div
              className="animate-spin w-8 h-8 border-[3px] border-teal-500 border-t-transparent rounded-full mx-auto mb-4"
              style={{ borderTopColor: "transparent" }}
            />
            <p className="text-slate-500">Loading your cases...</p>
          </div>
        ) : totalCasesCount === 0 ? (
          <EmptyState />
        ) : !hasResults ? (
          <NoResultsState
            searchTerm={searchTerm}
            quickFilter={quickFilter}
            onClear={handleClearFilters}
          />
        ) : (
          <div className="space-y-4">
            {CATEGORIES.map((category) => (
              <CaseCategorySection
                key={category.key}
                config={category}
                cases={groupedCases[category.key]}
                isExpanded={expandedCategories[category.key]}
                onToggle={() => toggleCategory(category.key)}
                onDeleteCase={handleDeleteClick}
                onStarCase={handleStar}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
